// App — top-level shell. Pulls runs via the /runs poller, fans out a live
// event stream per visible non-terminal run, owns drag/drop column state and
// the drawer + chat + settings modal.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DragEvent } from 'react';
import { Header } from './components/Header';
import { Column } from './components/Column';
import { Drawer } from './components/Drawer';
import type { DrawerAction } from './components/Drawer';
import { ChatPanel } from './components/ChatPanel';
import { PromptComposer } from './components/PromptComposer';
import type { DraftTicket } from './components/PromptComposer';
import { SettingsModal } from './components/SettingsModal';
import { useRuns } from './hooks/useRuns';
import { useTheme } from './hooks/useTheme';
import { useToast } from './hooks/useToast';
import { useRunEventStream } from './hooks/useRunEventStream';
import { useConfig } from './hooks/useConfig';
import { COLUMNS, findColumn } from './lib/columns';
import { isAllowedTransition, serverAction } from './lib/dragRules';
import { eventToLast } from './lib/runMapping';
import * as api from './lib/api';
import type { ApiEventLine, ColumnId, LastEvent, RunVM, StartRunTicket } from './lib/types';

function draftToStartRunTicket(d: DraftTicket): StartRunTicket {
  const repo: { url?: string; path?: string } = {};
  if (d.repoUrl) repo.url = d.repoUrl;
  if (d.repoPath) repo.path = d.repoPath;
  return {
    id: d.id,
    title: d.title,
    ...(d.description ? { description: d.description } : {}),
    acceptance_criteria: d.ac.map((a) => a.text).filter((t) => t.length > 0),
    execution_mode: d.mode,
    review_loops: d.loops,
    agent_provider: d.agent,
    review_provider: d.reviewer,
    ...(repo.url || repo.path ? { repo } : {}),
    ...(typeof d.maxBudgetUsd === 'number' ? { max_budget_usd: d.maxBudgetUsd } : {}),
  };
}

export function App() {
  const { runs, status, refresh } = useRuns();
  const { theme, toggle: toggleTheme } = useTheme();
  const { toast, show: showToast } = useToast();
  const { config } = useConfig();

  const [openId, setOpenId] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [hoverCol, setHoverCol] = useState<ColumnId | null>(null);
  const [wipBlock, setWipBlock] = useState<ColumnId | null>(null);
  const [chatCollapsed, setChatCollapsed] = useState(false);
  // Flips true the first time the operator sends a chat message; lets
  // PromptComposer one-shot auto-collapse so the screen makes room for
  // the chat once it's actively in use.
  const [chatEngaged, setChatEngaged] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [doneExpanded, setDoneExpanded] = useState(false);
  const [liveEvents, setLiveEvents] = useState<Record<string, LastEvent | null>>({});

  // M12 follow-up: detect runs transitioning to a terminal failed/escalated
  // state during the user's session and surface them with a toast +
  // auto-expand the Done column. Otherwise the run silently slides into the
  // collapsed Done badge and the user thinks the platform crashed.
  const prevStatusRef = useRef<Map<string, string>>(new Map());
  useEffect(() => {
    const next = new Map<string, string>();
    const newlyFailed: RunVM[] = [];
    for (const run of runs) {
      const prev = prevStatusRef.current.get(run.runId);
      next.set(run.runId, run.status);
      // Only flag transitions we observed (skip first-pass when prev is
      // undefined — that's the initial poll, not a state change). And only
      // flag fail / escalate / cancelled — succeeded with no PR is a
      // separate concern handled by the orchestrator now.
      if (
        prev !== undefined
        && prev !== run.status
        && (run.status === 'failed' || run.status === 'escalated')
      ) {
        newlyFailed.push(run);
      }
    }
    prevStatusRef.current = next;

    if (newlyFailed.length > 0) {
      const r = newlyFailed[0]!;
      const tail = newlyFailed.length > 1 ? ` (+${newlyFailed.length - 1} more)` : '';
      showToast(`run ${r.id} ${r.status}${tail} — expand done column for details`, 'warn');
      setDoneExpanded(true);
    }
  }, [runs, showToast]);

  const openRun = useMemo(
    () => runs.find((r) => r.runId === openId) ?? null,
    [runs, openId],
  );

  // Kanban filter: hide runs the user shouldn't see in any column (github-
  // webhook-initiated PR-mode duplicates of ticket-driven runs). `openRun`
  // above intentionally uses the unfiltered list so a direct drawer link
  // (e.g. shared URL) still resolves to a hidden run.
  const visibleRuns = useMemo(
    () => runs.filter((r) => !r.hiddenFromKanban),
    [runs],
  );

  // Subscribe to per-run event streams for cards in implementing/review.
  // We only run a handful at once to bound socket count.
  //
  // The currently-open drawer run is force-included when it's in an active
  // column, even if it's hidden from the kanban or sits past the first 12
  // visible cards. Without this, a shared/direct link to such a run shows a
  // stale drawer because the EventSource fan-out skipped it.
  const liveTargets = useMemo(
    () => {
      const ids = visibleRuns
        .filter((r) => r.column === 'implementing' || r.column === 'review')
        .slice(0, 12)
        .map((r) => r.runId);
      if (
        openRun
        && (openRun.column === 'implementing' || openRun.column === 'review')
        && !ids.includes(openRun.runId)
      ) {
        ids.push(openRun.runId);
      }
      return ids;
    },
    [visibleRuns, openRun],
  );

  const onAcceptDrafts = useCallback(async (ds: DraftTicket[], opts?: { stash?: boolean }) => {
    if (ds.length === 0) return;
    const stash = opts?.stash === true;
    let succeeded = 0;
    let lastRunId: string | null = null;
    for (const d of ds) {
      try {
        const ticket = draftToStartRunTicket(d);
        const res = stash ? await api.stashRun(ticket) : await api.startRun(ticket);
        succeeded++;
        lastRunId = res.run_id;
      } catch (err) {
        showToast(
          `failed to ${stash ? 'stash' : 'start run for'} ${d.id}: ${err instanceof Error ? err.message : String(err)}`,
          'error',
        );
      }
    }
    if (succeeded > 0) {
      const tail = ds.length === 1 && lastRunId
        ? ` · run ${lastRunId.slice(0, 8)}`
        : '';
      const verb = stash ? 'stashed' : 'queued';
      showToast(
        `${verb} ${succeeded}/${ds.length} ticket${succeeded === 1 ? '' : 's'}${tail}`,
        succeeded === ds.length ? 'info' : 'warn',
      );
      await refresh();
    }
  }, [showToast, refresh]);

  const onPromoteDraft = useCallback(async (runId: string) => {
    try {
      await api.promoteRun(runId);
      showToast('queued', 'info');
      await refresh();
    } catch (err) {
      showToast(`promote failed: ${err instanceof Error ? err.message : String(err)}`, 'error');
    }
  }, [showToast, refresh]);

  return (
    <div className="app">
      <Header
        runs={runs}
        queueMode={config?.queue_mode ?? null}
        providersConfigured={config?.providers_configured ?? []}
        theme={theme}
        toggleTheme={toggleTheme}
        onSettings={() => setShowSettings(true)}
      />
      <PromptComposer onAcceptDrafts={onAcceptDrafts} forceCollapsed={chatEngaged} />

      {status.kind === 'loading' && (
        <div className="boot-banner">
          <div className="boot-banner-title">connecting…</div>
        </div>
      )}

      {status.kind === 'no-persistence' && (
        <div className="boot-banner">
          <div className="boot-banner-title">platform is running without persistence</div>
          <div className="boot-banner-sub">
            /runs returned 503. Set DATABASE_URL on the review-loop server and re-run migrations
            so runs and events are stored. The fastest path:
          </div>
          <pre>docker compose up</pre>
        </div>
      )}

      {status.kind === 'unreachable' && (() => {
        // Authenticated cross-origin requests through Cloudflare Access fail
        // opaquely from the SPA's POV — the browser can't follow the cross-
        // origin redirect to /cdn-cgi/access/login, so the fetch surfaces a
        // TypeError with no response. Walk the operator through it.
        const authUrl = `${api.API_BASE_URL || window.location.origin}/health`;
        switch (status.category) {
          case 'cors-or-auth':
            return (
              <div className="boot-banner">
                <div className="boot-banner-title">not authenticated to API</div>
                <div className="boot-banner-sub">
                  Open{' '}
                  <a href={authUrl} target="_blank" rel="noreferrer">
                    <code>{authUrl}</code>
                  </a>
                  {' '}in a new tab, sign in via Cloudflare Access, then refresh this page.
                </div>
                <div className="boot-banner-sub boot-banner-detail">{status.message}</div>
              </div>
            );
          case 'origin-down':
            return (
              <div className="boot-banner">
                <div className="boot-banner-title">API server unreachable</div>
                <div className="boot-banner-sub">Check that review-loop is running.</div>
                <div className="boot-banner-sub boot-banner-detail">{status.message}</div>
              </div>
            );
          case 'server-error':
            return (
              <div className="boot-banner">
                <div className="boot-banner-title">
                  API returned {status.status ?? '5xx'}
                </div>
                {status.bodyExcerpt && (
                  <pre className="boot-banner-sub">{status.bodyExcerpt}</pre>
                )}
                <div className="boot-banner-sub boot-banner-detail">{status.message}</div>
              </div>
            );
          default:
            return (
              <div className="boot-banner">
                <div className="boot-banner-title">platform unreachable</div>
                <div className="boot-banner-sub">{status.message}</div>
                <div className="boot-banner-sub">
                  check that <code>review-loop</code> is running at the configured base URL
                  (VITE_API_BASE_URL, default proxy → <code>http://localhost:3000</code>).
                </div>
              </div>
            );
        }
      })()}

      {status.kind === 'ok' && (
        <Board
          runs={visibleRuns}
          openId={openId}
          setOpenId={setOpenId}
          draggingId={draggingId}
          setDraggingId={setDraggingId}
          hoverCol={hoverCol}
          setHoverCol={setHoverCol}
          wipBlock={wipBlock}
          setWipBlock={setWipBlock}
          doneExpanded={doneExpanded}
          setDoneExpanded={setDoneExpanded}
          liveEvents={liveEvents}
          setLiveEvents={setLiveEvents}
          liveTargets={liveTargets}
          chatCollapsed={chatCollapsed}
          setChatCollapsed={setChatCollapsed}
          onChatFirstSend={() => setChatEngaged(true)}
          showToast={showToast}
          refresh={refresh}
          openRun={openRun}
          onPromoteDraft={onPromoteDraft}
        />
      )}

      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
      {toast && <div className={`toast toast-${toast.tone}`}>{toast.message}</div>}
    </div>
  );
}

interface BoardProps {
  runs: RunVM[];
  openId: string | null;
  setOpenId: (id: string | null) => void;
  draggingId: string | null;
  setDraggingId: (id: string | null) => void;
  hoverCol: ColumnId | null;
  setHoverCol: (id: ColumnId | null) => void;
  wipBlock: ColumnId | null;
  setWipBlock: (id: ColumnId | null) => void;
  doneExpanded: boolean;
  setDoneExpanded: (v: boolean) => void;
  liveEvents: Record<string, LastEvent | null>;
  setLiveEvents: (fn: (prev: Record<string, LastEvent | null>) => Record<string, LastEvent | null>) => void;
  liveTargets: string[];
  chatCollapsed: boolean;
  setChatCollapsed: (v: boolean) => void;
  onChatFirstSend: () => void;
  showToast: (m: string, t?: 'info' | 'warn' | 'error') => void;
  refresh: () => Promise<void>;
  openRun: RunVM | null;
  onPromoteDraft: (runId: string) => void;
}

function Board(props: BoardProps) {
  // For each non-terminal run in implementing/review, mount a tiny stream
  // listener that keeps `liveEvents[runId]` warm.
  return (
    <div className="main">
      <div className="board">
        {props.liveTargets.map((id) => (
          <RunStreamSubscriber
            key={id}
            runId={id}
            onEvent={(line) => {
              props.setLiveEvents((prev) => ({ ...prev, [id]: eventToLast(line) }));
            }}
          />
        ))}
        {COLUMNS.map((col) => {
          const colRuns = props.runs.filter((r) => r.column === col.id);
          return (
            <ColumnContainer
              key={col.id}
              {...props}
              col={col}
              runs={colRuns}
            />
          );
        })}
      </div>
      {props.openRun && (
        <Drawer
          run={props.openRun}
          onClose={() => props.setOpenId(null)}
          onAction={(action, runId) => handleDrawerAction(action, runId, props)}
        />
      )}
      <ChatPanel
        runs={props.runs}
        collapsed={props.chatCollapsed}
        onToggleCollapsed={() => props.setChatCollapsed(!props.chatCollapsed)}
        onJumpToCard={props.setOpenId}
        onRefresh={props.refresh}
        onFirstSend={props.onChatFirstSend}
      />
    </div>
  );
}

function ColumnContainer(props: BoardProps & {
  col: typeof COLUMNS[number];
  runs: RunVM[];
}) {
  const { col } = props;

  const onDragStart = useCallback((e: DragEvent<HTMLDivElement>, run: RunVM) => {
    props.setDraggingId(run.runId);
    e.dataTransfer.effectAllowed = 'move';
    // dataTransfer needs a payload to register on Firefox.
    try { e.dataTransfer.setData('text/plain', run.runId); } catch { /* noop */ }
  }, [props]);

  const onDragEnd = useCallback(() => {
    props.setDraggingId(null);
    props.setHoverCol(null);
    props.setWipBlock(null);
  }, [props]);

  const onDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    props.setHoverCol(col.id);
    const dragId = props.draggingId;
    if (!dragId) return;
    const run = props.runs.find((r) => r.runId === dragId);
    if (!run) return;
    const colDef = findColumn(col.id);
    if (!colDef?.limit) { props.setWipBlock(null); return; }
    const incumbent = props.runs.filter((r) => r.column === col.id).length;
    if (incumbent >= colDef.limit && run.column !== col.id) {
      props.setWipBlock(col.id);
    } else {
      props.setWipBlock(null);
    }
  }, [props, col.id]);

  const onDragLeave = useCallback(() => {
    if (props.hoverCol === col.id) props.setHoverCol(null);
    if (props.wipBlock === col.id) props.setWipBlock(null);
  }, [props, col.id]);

  const onDrop = useCallback(async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const dragId = props.draggingId;
    if (!dragId) { onDragEnd(); return; }
    const run = props.runs.find((r) => r.runId === dragId);
    if (!run) { onDragEnd(); return; }
    const colDef = findColumn(col.id);
    const incumbent = props.runs.filter((r) => r.column === col.id && r.runId !== run.runId).length;
    if (colDef?.limit && incumbent >= colDef.limit) {
      props.showToast(`WIP limit reached on ${col.title}`, 'warn');
      onDragEnd();
      return;
    }
    if (!isAllowedTransition(run.column, col.id)) {
      onDragEnd();
      return;
    }
    const action = serverAction(run.column, col.id);
    if (action === 'cancel') {
      try {
        await api.cancelRun(run.runId);
        props.showToast(`cancelled ${run.id}`, 'info');
        await props.refresh();
      } catch (err) {
        props.showToast(`cancel failed: ${err instanceof Error ? err.message : String(err)}`, 'error');
      }
    } else {
      // start / mark-merged are not wired to a backend yet — silently no-op.
      // The transition allow-list is preserved so the cursor / drop affordance
      // still feels right; only the toast is removed since "coming soon" was
      // misleading.
    }
    onDragEnd();
  }, [props, col.id, col.title, onDragEnd]);

  return (
    <Column
      col={col}
      runs={props.runs}
      hoverActive={props.hoverCol === col.id}
      wipBlocked={props.wipBlock === col.id}
      doneExpanded={props.doneExpanded}
      setDoneExpanded={props.setDoneExpanded}
      selectedRunId={props.openId}
      draggingRunId={props.draggingId}
      liveEvents={props.liveEvents}
      onOpen={(id) => props.setOpenId(id)}
      onCancel={async (id) => {
        try {
          const result = await api.cancelRun(id);
          // Adapt toast to what the backend actually did: parked runs get
          // cancelled directly, dismissed terminal runs get a metadata stamp,
          // running runs get a flag for the worker to observe. Misreporting
          // "cancellation requested" for every case made the dismiss button
          // look broken when it was actually working.
          const message =
            result.status === 'cancelled' ? 'cancelled' :
            result.status === 'dismissed' ? 'dismissed from queue' :
            result.status === 'already-terminal' ? `already ${(result as { run_status?: string }).run_status ?? 'terminal'}` :
            'cancellation requested';
          props.showToast(message, 'info');
          await props.refresh();
        } catch (err) {
          props.showToast(`cancel failed: ${err instanceof Error ? err.message : String(err)}`, 'error');
        }
      }}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onPromote={props.onPromoteDraft}
    />
  );
}

async function handleDrawerAction(action: DrawerAction, runId: string, props: BoardProps): Promise<void> {
  if (action === 'cancel' || action === 'escalate') {
    try {
      const result = await api.cancelRun(runId);
      const message =
        result.status === 'cancelled' ? 'cancelled' :
        result.status === 'dismissed' ? 'dismissed from queue' :
        result.status === 'already-terminal' ? `already ${(result as { run_status?: string }).run_status ?? 'terminal'}` :
        'cancellation requested';
      props.showToast(message, 'info');
      await props.refresh();
    } catch (err) {
      props.showToast(`cancel failed: ${err instanceof Error ? err.message : String(err)}`, 'error');
    }
    return;
  }
  if (action === 'resume') {
    try {
      const res = await api.resumeRun(runId);
      props.showToast(`resumed as ${res.run_id.slice(0, 8)} (session ${res.session_id.slice(0, 12)}…)`, 'info');
      await props.refresh();
    } catch (err) {
      props.showToast(`resume failed: ${err instanceof Error ? err.message : String(err)}`, 'error');
    }
    return;
  }
}

// Tiny invisible component that owns one event subscription. Lets us hook
// per-run streams declaratively from inside the Board's render loop.
function RunStreamSubscriber(props: {
  runId: string;
  onEvent: (line: ApiEventLine) => void;
}): null {
  const { runId } = props;
  const onEventRef = useRef(props.onEvent);
  useEffect(() => { onEventRef.current = props.onEvent; }, [props.onEvent]);
  useRunEventStream({
    enabled: true,
    runId,
    onEvent: (line) => onEventRef.current(line),
  });
  return null;
}
